import { useCallback, useEffect, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  activeSpeakerIdAtom,
  activeSpeakerNameAtom,
  activeSpeakingAtom,
  autoPlayResolvedAtom,
  currentlyPlayingAtom,
  currentVisemeAtom,
} from '../atoms/speechAtoms'

// "narrator" -> "Narrator" for display; pass character names through unchanged.
function displayName(voiceLabel) {
  if (!voiceLabel) return null
  return voiceLabel === 'narrator' ? 'Narrator' : voiceLabel
}
import { speechService } from '../services/speechService'

// Normalize the viseme marks the backend returns INLINE in the speech response
// (a parsed [{ time, value }] array, time in ms, sorted). The backend sends them
// inline rather than as an S3 URL because the audio bucket has no CORS rule, so
// a browser fetch() of the JSON would be blocked even though <audio> can still
// play the MP3. Returns [] for anything missing/malformed → "mouth stays
// closed", never breaks audio.
function normalizeVisemes(marks) {
  if (!Array.isArray(marks)) return []
  return marks
    .map((m) => ({ time: Number(m?.time) || 0, value: m?.value || 'sil' }))
    .sort((a, b) => a.time - b.time)
}

// The viseme active at a given playback time (ms): the last mark whose time has
// passed. Linear scan is fine — a line has on the order of tens of marks.
function visemeAt(marks, timeMs) {
  let value = 'sil'
  for (let i = 0; i < marks.length; i++) {
    if (marks[i].time <= timeMs) value = marks[i].value
    else break
  }
  return value
}

// A single shared <audio> element for the whole player, so story beats and the
// chat sidebar can never play over each other. Created lazily in the browser.
let sharedAudio = null
function getAudio() {
  if (!sharedAudio && typeof Audio !== 'undefined') {
    sharedAudio = new Audio()
  }
  return sharedAudio
}

/**
 * Player speech controller. Owns the shared audio element and exposes play/stop
 * plus an auto-play queue. Each "segment" is a small descriptor:
 *
 *   { id, kind: 'beat'|'chat', sessionId?, beatId?, segmentIndex?,
 *     storyId?, characterId?, text? }
 *
 * `id` is a stable key used to show which segment is currently playing.
 *
 * Manual playback (playSegment / toggleSegment) is ALWAYS available — a player
 * can play or replay any line whenever they like, even with auto-play off. Only
 * the auto-play queue (playQueue) respects the resolved auto-play setting.
 */
export function useSpeech() {
  const autoPlay = useAtomValue(autoPlayResolvedAtom)
  const [playingId, setPlayingId] = useAtom(currentlyPlayingAtom)
  const setActiveSpeaker = useSetAtom(activeSpeakerIdAtom)
  const setActiveSpeaking = useSetAtom(activeSpeakingAtom)
  const setActiveSpeakerName = useSetAtom(activeSpeakerNameAtom)
  const setCurrentViseme = useSetAtom(currentVisemeAtom)

  // The viseme marks for the segment currently playing, and the rAF handle that
  // walks them against audio.currentTime. Refs (not state) so the animation loop
  // never triggers React renders except via the viseme atom it sets.
  const visemeMarksRef = useRef([])
  const rafRef = useRef(null)

  // The active play "token" — bumped on every new play/stop so a slow fetch that
  // resolves after the user moved on doesn't hijack the audio element.
  const tokenRef = useRef(0)
  const queueRef = useRef([])
  // Ref to the queue runner so toggleSegment can call it before runQueue is
  // declared below (the two reference each other; a ref breaks the cycle).
  const runQueueRef = useRef(null)
  // The total segment count reported by the last-resolved beat segment. A
  // narration beat can split into narrator + per-character voice segments on the
  // backend; the player only learns the count from the response, so playQueue
  // uses this to enqueue the remaining segments after the first one plays.
  const lastSegmentCountRef = useRef(1)

  const resolveUrl = useCallback(async (segment) => {
    if (segment.kind === 'chat') {
      return speechService.speakChatMessage(segment.storyId, segment.characterId, segment.text)
    }
    return speechService.speakBeatSegment(segment.sessionId, segment.beatId, segment.segmentIndex ?? 0)
  }, [])

  // Stop walking the viseme marks and close the mouth. Safe to call repeatedly.
  const stopVisemeLoop = useCallback(() => {
    if (rafRef.current != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(rafRef.current)
    }
    rafRef.current = null
    visemeMarksRef.current = []
    setCurrentViseme('sil')
  }, [setCurrentViseme])

  // Begin driving currentVisemeAtom from audio.currentTime for the active
  // segment. No-op (mouth stays closed) when the segment has no viseme marks.
  const startVisemeLoop = useCallback((audio) => {
    if (typeof requestAnimationFrame === 'undefined') return
    const tick = () => {
      if (!visemeMarksRef.current.length) { rafRef.current = null; return }
      setCurrentViseme(visemeAt(visemeMarksRef.current, audio.currentTime * 1000))
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [setCurrentViseme])

  const stop = useCallback(() => {
    tokenRef.current += 1
    queueRef.current = []
    const audio = getAudio()
    if (audio) {
      audio.pause()
      audio.removeAttribute('src')
    }
    setPlayingId(null)
    setActiveSpeaker(null)
    setActiveSpeaking(false)
    setActiveSpeakerName(null)
    stopVisemeLoop()
  }, [setPlayingId, setActiveSpeaker, setActiveSpeaking, setActiveSpeakerName, stopVisemeLoop])

  // Play one segment. Resolves to true when playback finished naturally, false
  // if it was superseded/cancelled or errored (so a queue can decide to stop).
  const playSegment = useCallback(async (segment) => {
    const audio = getAudio()
    if (!audio) return false

    const myToken = (tokenRef.current += 1)
    setPlayingId(segment.id)

    let data
    try {
      data = await resolveUrl(segment)
    } catch {
      if (tokenRef.current === myToken) setPlayingId(null)
      return false
    }
    // Superseded while the URL was being fetched.
    if (tokenRef.current !== myToken) return false

    // Remember how many segments this beat actually has (default 1), so an
    // auto-play queue can pick up the rest.
    if (segment.kind === 'beat') {
      lastSegmentCountRef.current = Number(data.segment_count) || 1
    }

    // Which portrait animates: the voicing character (null for narrator). The
    // viseme marks (if any) drive its mouth; absent marks => mouth stays closed.
    // Marks arrive inline in the response — no extra (CORS-blocked) fetch.
    //
    // activeSpeaking flags that a voice is playing AT ALL (narrator included),
    // which is what the visualizer keys off; activeSpeakerName is the label
    // shown under the portrait ("Narrator" for narrator segments).
    setActiveSpeaker(data.character_id ?? segment.characterId ?? null)
    setActiveSpeaking(true)
    setActiveSpeakerName(displayName(data.voice_label) ?? segment.characterName ?? null)
    visemeMarksRef.current = normalizeVisemes(data.visemes)

    return new Promise((resolve) => {
      const finish = (ok) => {
        audio.onended = null
        audio.onerror = null
        stopVisemeLoop()
        if (tokenRef.current === myToken) {
          setPlayingId(null)
          setActiveSpeaker(null)
          setActiveSpeaking(false)
          setActiveSpeakerName(null)
        }
        resolve(ok)
      }
      audio.onended = () => finish(true)
      audio.onerror = () => finish(false)
      audio.src = data.url
      audio.play().then(() => {
        if (tokenRef.current === myToken) startVisemeLoop(audio)
      }).catch(() => finish(false))
    })
  }, [resolveUrl, setPlayingId, setActiveSpeaker, setActiveSpeaking, setActiveSpeakerName, startVisemeLoop, stopVisemeLoop])

  // Toggle: clicking the playing segment stops it; clicking another plays it.
  // For a beat's index-0 segment this plays the WHOLE beat (all of its voice
  // segments in order) — a narration beat can split into narrator + character
  // voices, and a manual click should read the whole thing.
  const toggleSegment = useCallback((segment) => {
    if (playingId === segment.id) {
      stop()
    } else if (segment.kind === 'beat' && (segment.segmentIndex ?? 0) === 0) {
      runQueueRef.current?.([segment])
    } else {
      playSegment(segment)
    }
  }, [playingId, stop, playSegment])

  // Play a list of segments in order, expanding a beat's index-0 segment into
  // its full set once the backend reports segment_count. Stops if any segment is
  // cancelled/superseded. Shared by auto-play (playQueue) and manual beat play.
  //
  // A narration beat is enqueued as a single segment (index 0) by the builder,
  // because the player can't know up front how many voice segments it splits
  // into. After segment 0 plays, the reported segment_count tells us how many
  // remain; we splice them in for the same beat so narrator/character voices
  // play in order.
  const runQueue = useCallback(async (segments) => {
    if (!segments?.length) return
    stop()
    queueRef.current = [...segments]

    let i = 0
    while (i < queueRef.current.length) {
      const segment = queueRef.current[i]
      const ok = await playSegment(segment)
      // playSegment returns false when cancelled/superseded/errored — stop here.
      if (!ok) break
      // Expand a single-segment beat into its full set once we know the count.
      if (
        segment.kind === 'beat' &&
        (segment.segmentIndex ?? 0) === 0 &&
        !queueRef.current.some((s) => s.beatId === segment.beatId && (s.segmentIndex ?? 0) === 1) &&
        lastSegmentCountRef.current > 1
      ) {
        const extra = []
        for (let s = 1; s < lastSegmentCountRef.current; s++) {
          extra.push({
            id: `beat:${segment.beatId}:${s}`,
            kind: 'beat',
            sessionId: segment.sessionId,
            beatId: segment.beatId,
            segmentIndex: s,
          })
        }
        queueRef.current.splice(i + 1, 0, ...extra)
      }
      i += 1
    }
  }, [stop, playSegment])

  // Point the ref at the latest runQueue so toggleSegment (declared earlier) can
  // invoke it without a declaration-order cycle. Updated in an effect so we never
  // touch the ref during render.
  useEffect(() => {
    runQueueRef.current = runQueue
  }, [runQueue])

  // Auto-play entry point: gated on the resolved auto-play setting.
  const playQueue = useCallback((segments) => {
    if (!autoPlay) return
    return runQueue(segments)
  }, [autoPlay, runQueue])

  // Stop on unmount.
  useEffect(() => () => stop(), [stop])

  return {
    autoPlay,
    playingId,
    playSegment,
    toggleSegment,
    playQueue,
    stop,
  }
}
